import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faMobileAlt,
  faCreditCard,
  faWallet,
  faEnvelope,
  faMoneyBillWave,
  faTimesCircle,
  faSpinner,
  faInfoCircle,
  faHandHoldingUsd // Added for deposits theme
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";

const DepositsPage = () => {
  const [balance, setBalance] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [amount, setAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [depositAmount, setDepositAmount] = useState(0); // This is the net amount after fees
  const [isLoading, setIsLoading] = useState(false);
  const [webUrl, setWebUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("ipay"); // State to handle the selected payment method

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  let userId = null;

  try {
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      userId = userData.unique;
    }
  } catch (e) {
    console.error("Error parsing user data from localStorage:", e);
    // Handle parsing error, e.g., redirect to login
  }

  // Effect to fetch user balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!token || !userId) {
        toast.warn("Authentication details missing. Please log in.");
        return;
      }
      try {
        const response = await axios.get(`https://backend.inyumba.co.ke/iNyumba/balance/user/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setBalance(
          response.data.response && response.data.response.length > 0
            ? response.data.response[0].balance
            : 0
        );
      } catch (error) {
        console.error("Error fetching balance:", error);
        toast.error("Failed to fetch balance.");
      }
    };

    fetchBalance();
  }, [token, userId]);

  // Effect to calculate deposit amount after fees
  useEffect(() => {
    const enteredAmount = parseFloat(amount);
    if (!isNaN(enteredAmount) && enteredAmount > 0) {
      if (paymentMethod === "ipay") {
        setDepositAmount((enteredAmount * 0.98).toFixed(2)); // 2% charge for iPay
      } else if (paymentMethod === "mpesa") {
        setDepositAmount((enteredAmount * 0.99).toFixed(2)); // 1% charge for M-Pesa
      }
    } else {
      setDepositAmount(0);
    }
  }, [amount, paymentMethod]);

  const toggleShowBalance = () => {
    setShowBalance(!showBalance);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!token || !userId) {
      toast.warn("Authentication details missing. Please log in.");
      setIsLoading(false);
      return;
    }

    const apiUrl =
      paymentMethod === "ipay"
        ? `https://backend.inyumba.co.ke/iNyumba/initiatedepositpayment/${userId}`
        : `https://backend.inyumba.co.ke/iNyumba/user/mpesapayment/tenanttopupinitiatestk/${userId}`;

    try {
      const response = await axios.post(
        apiUrl,
        {
          amount: parseFloat(amount), // Ensure amount is a number
          phone_number: phoneNumber,
          email: paymentMethod === "ipay" ? email : undefined, // Only send email for iPay
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        if (paymentMethod === "ipay") {
          setWebUrl(response.data.response);
          toast.success("iPay payment initiated successfully! Redirecting...");
          setShowIframe(true);
        } else {
          // M-Pesa
          if (response.data.response === "Kindly input your pin on mpesa pop up") {
            toast.info("M-Pesa STK Push sent! Kindly input your PIN on the M-Pesa pop-up.");
            setShowIframe(false);
          } else {
            toast.success("M-Pesa payment initiated successfully!");
            setShowIframe(false);
          }
        }
        setAmount(""); // Clear form fields on success
        setPhoneNumber("");
        setEmail("");
      } else {
        toast.error(response.data.response || "Payment initiation failed! Please try again.");
      }
    } catch (error) {
      console.error("Payment submission error:", error);
      if (error.response && error.response.data && error.response.data.response) {
        toast.error(error.response.data.response);
      } else {
        toast.error("An unexpected error occurred during payment. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseIframe = () => {
    setShowIframe(false);
    setWebUrl("");
    // Optionally, re-fetch balance after closing iframe
    // navigate("/user/deposits"); // This might refresh the whole page, consider carefully
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
        
        <div className="max-w-4xl mx-auto py-8 text-center animate-fade-in-down">
          <div className="flex items-center justify-center mb-6">
            <FontAwesomeIcon icon={faHandHoldingUsd} className="text-indigo-600 text-6xl mr-4" />
            <h1 className="text-5xl font-extrabold text-gray-900">
              Your Deposits
            </h1>
          </div>
          <p className="text-gray-600 text-lg mb-8">
            Easily add funds to your account balance using your preferred payment method.
          </p>

          {/* Balance Display Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 flex items-center justify-between mb-8 border border-indigo-200 animate-fade-in">
            <div className="flex items-center space-x-4">
              <FontAwesomeIcon icon={faWallet} className="text-indigo-500 text-4xl" />
              <p className="text-xl font-semibold text-gray-700">
                Current Balance:
              </p>
            </div>
            <div className="flex items-center">
              <p className="text-indigo-700 text-4xl font-extrabold mr-4">
                {showBalance ? (
                  balance !== null ? `Ksh ${balance.toLocaleString()}` : "Ksh 0"
                ) : (
                  "********"
                )}
              </p>
              <button
                onClick={toggleShowBalance}
                className="text-gray-500 hover:text-indigo-600 transition-colors duration-200 relative group"
                aria-label={showBalance ? "Hide balance" : "Show balance"}
              >
                <FontAwesomeIcon icon={showBalance ? faEyeSlash : faEye} className="text-2xl" />
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-max px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {showBalance ? "Hide Balance" : "Show Balance"}
                </span>
              </button>
            </div>
          </div>

          {/* Deposit Form Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 animate-fade-in-up">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Make a New Deposit</h2>

            {/* Payment Method Selection Tabs */}
            <div className="flex bg-gray-100 rounded-full p-1 mb-8 shadow-inner">
              <button
                type="button"
                className={`flex-1 py-3 px-4 text-center text-lg font-semibold rounded-full cursor-pointer transition-all duration-300 flex items-center justify-center space-x-2
                  ${paymentMethod === "ipay" ? "bg-indigo-600 text-white shadow-md" : "text-gray-700 hover:bg-gray-200"}`}
                onClick={() => setPaymentMethod("ipay")}
              >
                <FontAwesomeIcon icon={faCreditCard} className="text-xl" />
                <span>iPay (Card/Bank)</span>
              </button>
              <button
                type="button"
                className={`flex-1 py-3 px-4 text-center text-lg font-semibold rounded-full cursor-pointer transition-all duration-300 flex items-center justify-center space-x-2
                  ${paymentMethod === "mpesa" ? "bg-green-600 text-white shadow-md" : "text-gray-700 hover:bg-gray-200"}`}
                onClick={() => setPaymentMethod("mpesa")}
              >
                <FontAwesomeIcon icon={faMobileAlt} className="text-xl" />
                <span>M-Pesa (STK Push)</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Conditional Email input for iPay */}
              {paymentMethod === "ipay" && (
                <div>
                  <label htmlFor="email" className="block text-gray-700 text-lg font-semibold mb-2 text-left">
                    Email Address
                  </label>
                  <div className="relative">
                    <FontAwesomeIcon icon={faEnvelope} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full px-5 py-3 pl-12 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-lg text-gray-900 placeholder-gray-400"
                      placeholder="e.g., your.email@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>
              )}

              {/* Phone Number input (for both, but explicitly visible here) */}
              <div>
                <label htmlFor="phoneNumber" className="block text-gray-700 text-lg font-semibold mb-2 text-left">
                  Phone Number
                </label>
                <div className="relative">
                  <FontAwesomeIcon icon={faMobileAlt} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="block w-full px-5 py-3 pl-12 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-lg text-gray-900 placeholder-gray-400"
                    placeholder="Use 254... or 07..., do not add +"
                    required
                    autoComplete="tel"
                  />
                </div>
              </div>

              {/* Amount input */}
              <div>
                <label htmlFor="amount" className="block text-gray-700 text-lg font-semibold mb-2 text-left">
                  Amount to Deposit (Ksh)
                </label>
                <div className="relative">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    id="amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="block w-full px-5 py-3 pl-12 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-lg text-gray-900 placeholder-gray-400"
                    placeholder="Enter amount (e.g., 1000)"
                    required
                    min="1" // Ensure positive amount
                  />
                </div>
              </div>

              {/* Total Deposit Amount (after fees) */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 flex justify-between items-center">
                <label className="text-xl font-bold text-gray-800">
                  Net Amount You Will Receive:
                </label>
                <p className="text-green-700 text-2xl font-extrabold">
                  Ksh {parseFloat(depositAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  className={`w-full flex items-center justify-center space-x-3
                    bg-gradient-to-r from-indigo-600 to-blue-700 text-white font-bold
                    py-3 px-8 rounded-full text-xl shadow-lg
                    hover:from-indigo-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105
                    focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:ring-offset-2
                    ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="text-white text-2xl" />
                  ) : (
                    <FontAwesomeIcon icon={faHandHoldingUsd} />
                  )}
                  <span>{isLoading ? "Processing..." : "Deposit Now"}</span>
                </button>
              </div>
            </form>

            {/* iFrame Display for iPay */}
            {showIframe && webUrl && (
              <div className="mt-8 bg-gray-100 rounded-lg shadow-inner p-4 relative border border-gray-300">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-indigo-500" /> Complete iPay Payment
                </h3>
                <p className="text-gray-600 mb-4 text-center">
                  You are being redirected to a secure payment gateway.
                </p>
                <button
                  className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-5 rounded-lg shadow-md transition-colors duration-200 flex items-center space-x-2"
                  onClick={handleCloseIframe}
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                  <span>Close Payment</span>
                </button>
                <iframe
                  src={webUrl}
                  width="100%"
                  height="500"
                  title="Payment Gateway"
                  className="rounded-lg border-2 border-gray-300"
                  sandbox="allow-scripts allow-popups allow-forms allow-same-origin" // Recommended sandbox attributes
                />
                <p className="text-center text-sm text-gray-500 mt-4">
                  After completing payment, you can close this window.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthGuard>
  );
};

export default DepositsPage;